/* eslint-disable import/no-cycle */
import {
  ConfigurationPanelStructure,
  ConfigurationPanelStructureItemEnum,
  TabItem,
  TemplateParentTypeEnum,
  TemplateStructureEnum,
  WidgetTypeConfig,
  WidgetTypeEnum,
} from '@/types';

// Routeurs (dispatch vers email/display selon templateType)
import getWidgetTextConfig from '@/components/template-builder/config/widgets/widget-text-config';
import getWidgetImageConfig from '@/components/template-builder/config/widgets/widget-image-config';
import getWidgetSeparatorConfig from '@/components/template-builder/config/widgets/widget-separator-config';
import getWidgetSpacerConfig from '@/components/template-builder/config/widgets/widget-spacer-config';
import getWidgetButtonConfig from '@/components/template-builder/config/widgets/widget-button-config';
import getWidgetSocialMediaConfig from '@/components/template-builder/config/widgets/widget-social-media-config';
import getWidgetSmartProductListConfig from '@/components/template-builder/config/widgets/widget-smart-product-list-config';
import getWidgetCountdownConfig from '@/components/template-builder/config/widgets/widget-countdown-config';
import getWidgetMenuConfig from '@/components/template-builder/config/widgets/widget-menu-config';

// Widgets partagés V3+V4
import widgetCodeConfig from '@/components/template-builder/config/widgets/widget-code-config';
import getWidgetTitleConfig from '@/components/template-builder/config/widgets/widget-title-config';
import widgetShareSocialMediaConfig from '@/components/template-builder/config/widgets/widget-share-social-media-config';
import widgetVoucherConfig from '@/components/template-builder/config/widgets/widget-voucher-config';
import widgetImportZipConfig from '@/components/template-builder/config/widgets/widget-import-zip-config';

// Widgets Display V3 uniquement
import widgetCreateAccountConfig from '@/components/template-builder/config/templates/display/widgets/widget-create-account-config';
import widgetNewsletterSubscriptionConfig from '@/components/template-builder/config/templates/display/widgets/widget-newsletter-subscription';
import widgetNewsletterUnsubscriptionConfig from '@/components/template-builder/config/templates/display/widgets/widget-newsletter-unsubscription';
import widgetFacebookMessengerConfig from '@/components/template-builder/config/templates/display/widgets/widget-facebook-messenger';

// Widgets Display V4 uniquement
import widgetJavascriptConfigV4 from '@/components/template-builder/config/templates/display-v4/widgets/widget-javascript-config';
import widgetInputTextConfigV4 from '@/components/template-builder/config/templates/display-v4/widgets/widget-input-text-config';
import widgetTextareaConfigV4 from '@/components/template-builder/config/templates/display-v4/widgets/widget-textarea-config';
import widgetInputDateConfigV4 from '@/components/template-builder/config/templates/display-v4/widgets/widget-input-date-config';
import widgetInputCheckboxConfigV4 from '@/components/template-builder/config/templates/display-v4/widgets/widget-input-checkbox-config';
import widgetInputRadioConfigV4 from '@/components/template-builder/config/templates/display-v4/widgets/widget-input-radio-config';
import widgetInputSelectConfigV4 from '@/components/template-builder/config/templates/display-v4/widgets/widget-input-select-config';
import widgetInputEmailConfigV4 from '@/components/template-builder/config/templates/display-v4/widgets/widget-input-email-config';
import widgetInputPasswordConfigV4 from '@/components/template-builder/config/templates/display-v4/widgets/widget-input-password-config';
import widgetInputPhoneConfigV4 from '@/components/template-builder/config/templates/display-v4/widgets/widget-input-phone-config';

// Game widgets V4
import widgetGameWheelConfigV4 from '@/components/template-builder/config/templates/display-v4/widgets/widget-game-wheel-config';
import widgetGameSlotMachineConfigV4 from '@/components/template-builder/config/templates/display-v4/widgets/widget-game-slot-machine-config';
import widgetGameScratchCardConfigV4 from '@/components/template-builder/config/templates/display-v4/widgets/widget-game-scratch-card-config';
import widgetConsentCheckboxConfigV4 from '@/components/template-builder/config/templates/display-v4/widgets/widget-consent-checkbox-config';
import widgetDisclosureConfigV4 from '@/components/template-builder/config/templates/display-v4/widgets/widget-disclosure-config';

import deepcopy from 'deepcopy';

type WidgetConfigMap = {
  [key: string]: WidgetTypeConfig;
};

// V3 Widget Config Map (display V3 only widgets)
const getWidgetConfigMap = (templateType: TemplateParentTypeEnum): WidgetConfigMap => ({
  // Routeurs (isV4 = false)
  [WidgetTypeEnum.SEPARATOR]: getWidgetSeparatorConfig(templateType, false),
  [WidgetTypeEnum.SPACER]: getWidgetSpacerConfig(templateType, false),
  [WidgetTypeEnum.BUTTON]: getWidgetButtonConfig(templateType, false),
  [WidgetTypeEnum.GDPR_BUTTON]: getWidgetButtonConfig(templateType, false),
  [WidgetTypeEnum.TEXT]: getWidgetTextConfig(templateType, false),
  [WidgetTypeEnum.IMAGE]: getWidgetImageConfig(templateType, false),
  [WidgetTypeEnum.SOCIAL_MEDIA]: getWidgetSocialMediaConfig(templateType, false),
  [WidgetTypeEnum.SMART_PRODUCT_LIST]: getWidgetSmartProductListConfig(templateType, false),
  // Partagés V3+V4
  [WidgetTypeEnum.TITLE]: getWidgetTitleConfig(templateType, false),
  [WidgetTypeEnum.CODE]: widgetCodeConfig,
  [WidgetTypeEnum.VOUCHER]: widgetVoucherConfig,
  [WidgetTypeEnum.SHARE_SOCIAL_MEDIA]: widgetShareSocialMediaConfig,
  [WidgetTypeEnum.IMPORT_ZIP]: widgetImportZipConfig,
  // Display V3 uniquement
  [WidgetTypeEnum.CREATE_ACCOUNT]: widgetCreateAccountConfig,
  [WidgetTypeEnum.NEWSLETTER_SUBSCRIPTION]: widgetNewsletterSubscriptionConfig,
  [WidgetTypeEnum.NEWSLETTER_UNSUBSCRIPTION]: widgetNewsletterUnsubscriptionConfig,
  [WidgetTypeEnum.FACEBOOK_MESSENGER]: widgetFacebookMessengerConfig,
});

const getWidgetConfig = (
  templateType: TemplateParentTypeEnum, widgetType: WidgetTypeEnum, columnTabItemConfig: TabItem, sectionTabItemConfig?: TabItem, lineTabItemConfig?: TabItem,
): ConfigurationPanelStructure => {
  const widgetTypeConfig = deepcopy(getWidgetConfigMap(templateType)[widgetType]);

  const items: Array<TabItem> = [
    {
      type: ConfigurationPanelStructureItemEnum.TAB_ITEM,
      tabItemType: TemplateStructureEnum.WIDGET,
      label: 'templateBuilder.configs.widget',
      ...widgetTypeConfig,
    },
  ];

  if (columnTabItemConfig && widgetType !== WidgetTypeEnum.IMPORT_ZIP) {
    if (items.length && columnTabItemConfig.tabPanel) {
      // eslint-disable-next-line no-unused-expressions
      items[0].tabPanel?.items.push(
        ...columnTabItemConfig.tabPanel?.items,
      );
    }
  }

  if (lineTabItemConfig && widgetType !== WidgetTypeEnum.IMPORT_ZIP) {
    if (items.length && lineTabItemConfig.tabPanel) {
      // eslint-disable-next-line no-unused-expressions
      items[0].tabPanel?.items.push(
        ...lineTabItemConfig.tabPanel?.items,
      );
    }
  }

  // In case of widget edition, we do not display the section's settings
  if (!widgetType && sectionTabItemConfig) {
    if (items.length && sectionTabItemConfig.tabPanel) {
      // eslint-disable-next-line no-unused-expressions
      items[0].tabPanel?.items.push(
        ...sectionTabItemConfig.tabPanel?.items,
      );
    }
  }

  return { items };
};

// V4 Widget Config Map (display V4 only widgets)
const getWidgetConfigMapV4 = (templateType: TemplateParentTypeEnum): WidgetConfigMap => ({
  // Routeurs (isV4 = true)
  [WidgetTypeEnum.SEPARATOR]: getWidgetSeparatorConfig(templateType, true),
  [WidgetTypeEnum.SPACER]: getWidgetSpacerConfig(templateType, true),
  [WidgetTypeEnum.BUTTON]: getWidgetButtonConfig(templateType, true),
  [WidgetTypeEnum.GDPR_BUTTON]: getWidgetButtonConfig(templateType, true),
  [WidgetTypeEnum.TEXT]: getWidgetTextConfig(templateType, true),
  [WidgetTypeEnum.IMAGE]: getWidgetImageConfig(templateType, true),
  [WidgetTypeEnum.SOCIAL_MEDIA]: getWidgetSocialMediaConfig(templateType, true),
  [WidgetTypeEnum.SMART_PRODUCT_LIST]: getWidgetSmartProductListConfig(templateType, true),
  [WidgetTypeEnum.COUNTDOWN]: getWidgetCountdownConfig(templateType),
  [WidgetTypeEnum.MENU]: getWidgetMenuConfig(templateType, true),
  // Partagés V3+V4
  [WidgetTypeEnum.TITLE]: getWidgetTitleConfig(templateType, true),
  [WidgetTypeEnum.CODE]: widgetCodeConfig,
  [WidgetTypeEnum.VOUCHER]: widgetVoucherConfig,
  [WidgetTypeEnum.SHARE_SOCIAL_MEDIA]: widgetShareSocialMediaConfig,
  [WidgetTypeEnum.IMPORT_ZIP]: widgetImportZipConfig,
  // Display V4 uniquement
  [WidgetTypeEnum.JAVASCRIPT]: widgetJavascriptConfigV4,
  [WidgetTypeEnum.INPUT_TEXT]: widgetInputTextConfigV4,
  [WidgetTypeEnum.INPUT_EMAIL]: widgetInputEmailConfigV4,
  [WidgetTypeEnum.INPUT_PASSWORD]: widgetInputPasswordConfigV4,
  [WidgetTypeEnum.INPUT_PHONE]: widgetInputPhoneConfigV4,
  [WidgetTypeEnum.TEXTAREA]: widgetTextareaConfigV4,
  [WidgetTypeEnum.INPUT_DATE]: widgetInputDateConfigV4,
  [WidgetTypeEnum.INPUT_CHECKBOX]: widgetInputCheckboxConfigV4,
  [WidgetTypeEnum.INPUT_RADIO]: widgetInputRadioConfigV4,
  [WidgetTypeEnum.INPUT_SELECT]: widgetInputSelectConfigV4,
  [WidgetTypeEnum.CONSENT_CHECKBOX]: widgetConsentCheckboxConfigV4,
  [WidgetTypeEnum.DISCLOSURE]: widgetDisclosureConfigV4,
  // Game widgets V4
  [WidgetTypeEnum.GAME_WHEEL]: widgetGameWheelConfigV4,
  [WidgetTypeEnum.GAME_SLOT_MACHINE]: widgetGameSlotMachineConfigV4,
  [WidgetTypeEnum.GAME_SCRATCH_CARD]: widgetGameScratchCardConfigV4,
});

export const getWidgetConfigV4 = (
  templateType: TemplateParentTypeEnum, widgetType: WidgetTypeEnum, columnTabItemConfig: TabItem, sectionTabItemConfig?: TabItem, lineTabItemConfig?: TabItem,
): ConfigurationPanelStructure => {
  const widgetTypeConfig = deepcopy(getWidgetConfigMapV4(templateType)[widgetType]);

  const items: Array<TabItem> = [
    {
      type: ConfigurationPanelStructureItemEnum.TAB_ITEM,
      tabItemType: TemplateStructureEnum.WIDGET,
      label: 'templateBuilder.configs.widget',
      ...widgetTypeConfig,
    },
  ];

  if (columnTabItemConfig && widgetType !== WidgetTypeEnum.IMPORT_ZIP) {
    if (items.length && columnTabItemConfig.tabPanel) {
      // eslint-disable-next-line no-unused-expressions
      items[0].tabPanel?.items.push(
        ...columnTabItemConfig.tabPanel?.items,
      );
    }
  }

  if (lineTabItemConfig && widgetType !== WidgetTypeEnum.IMPORT_ZIP) {
    if (items.length && lineTabItemConfig.tabPanel) {
      // eslint-disable-next-line no-unused-expressions
      items[0].tabPanel?.items.push(
        ...lineTabItemConfig.tabPanel?.items,
      );
    }
  }

  // In case of widget edition, we do not display the section's settings
  if (!widgetType && sectionTabItemConfig) {
    if (items.length && sectionTabItemConfig.tabPanel) {
      // eslint-disable-next-line no-unused-expressions
      items[0].tabPanel?.items.push(
        ...sectionTabItemConfig.tabPanel?.items,
      );
    }
  }

  return { items };
};

export default getWidgetConfig;
